import { ui } from '@procuros/datachecks';

import { parseFlexibleDate } from 'support/helpers/dateTime/dateTime';
import { getValidNumberValue } from 'support/helpers/numbers/numbers';
import { FileInputErrorCodes } from 'components/Form/FileInput/FileInput';

export type SheetExpectedData = {
  headers: Array<string>;
  formats: Record<string, ui.FieldTypes>;
};

export enum TemplateValidationErrorCodes {
  INVALID_HEADERS = 'INVALID_HEADERS',
  INVALID_DATA_FORMATS = 'INVALID_DATA_FORMATS',
}

export type RowDataType = Record<string, unknown>;

export type ValidationErrorDetail = {
  row: number;
  column: string;
  value: string;
  expectedFormat: string;
};

// Map of expected data formats for each column type
export const COLUMNS_DATA_FORMATS_MAP: Record<ui.FieldTypes, (value: unknown) => boolean> = {
  [ui.FieldTypes.text]: (value) => typeof value === 'string' || typeof value === 'number',
  [ui.FieldTypes.url]: (value) => typeof value === 'string',
  [ui.FieldTypes.boolean]: (value) =>
    (typeof value === 'string' && ['true', 'false'].includes(value.toLocaleLowerCase())) || typeof value === 'boolean',
  [ui.FieldTypes.number]: (value) =>
    typeof value === 'number' || (typeof value === 'string' && !isNaN(getValidNumberValue(value))),
  [ui.FieldTypes.monetary]: (value) =>
    typeof value === 'number' || (typeof value === 'string' && !isNaN(getValidNumberValue(value))),
  [ui.FieldTypes.date]: (value) => {
    if (value instanceof Date) {
      return true;
    }
    try {
      parseFlexibleDate(value as string);
      return true;
    } catch {
      return false;
    }
  },
  [ui.FieldTypes.date_time]: (value) => {
    if (value instanceof Date) {
      return true;
    }
    try {
      parseFlexibleDate(value as string);
      return true;
    } catch {
      return false;
    }
  },
  [ui.FieldTypes.select]: (value) => typeof value === 'string',
  //Not used in here
  [ui.FieldTypes.multi_select]: () => false,
  [ui.FieldTypes.multi_column]: () => false,
  [ui.FieldTypes.long_text]: () => false,
  [ui.FieldTypes.password]: () => false,
  [ui.FieldTypes.radio]: () => false,
  [ui.FieldTypes.text_array]: () => false,
  [ui.FieldTypes.external_action]: () => false,
  [ui.FieldTypes.file_upload]: () => false,
};

export type FileUploadError =
  | {
      code: FileInputErrorCodes | 'UNKNOWN';
    }
  | {
      code: TemplateValidationErrorCodes;
      columns: Array<string>;
      errorDetails?: Array<ValidationErrorDetail>;
    };

export class UploadValidationError extends Error {
  code: TemplateValidationErrorCodes;
  columns?: Array<string>;
  errorDetails?: Array<ValidationErrorDetail>;

  constructor(
    code: TemplateValidationErrorCodes,
    columns?: Array<string>,
    errorDetails?: Array<ValidationErrorDetail>,
  ) {
    super(code);
    this.code = code;
    this.columns = columns;
    this.errorDetails = errorDetails;

    Object.setPrototypeOf(this, UploadValidationError.prototype);
  }
}
